import { IPublicClientApplication } from '@azure/msal-browser';
import { AuthConfig } from './AuthConfig';
import { getCookie, setCookie } from './CookieUtils';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import { SERVER_ERROR_MESSAGES } from '../Constants/Constants';
import { fbLogEvent } from '../Helpers/FirebaseHelper';
import { EVENT, LEAD, SCREEN } from '../Constants/AnalyticsConstants';

/**
 * @module TokenManager
 * @description Provides utility functions for managing access tokens from Azure AD.
 *
 * @typedef {Object} TokenManager
 * @property {function} init - Initializes the TokenManager and retrieves an access token.
 */
export const TokenManager = {
  init: (MSALInstance: IPublicClientApplication) =>
    new Promise((res) => {
      const storedToken = getCookie('id_token');
      if (storedToken) {
        res(storedToken);
      } else {
        getTokenFromAzure(MSALInstance).then((token: any) => {
          res(token);
        });
      }
    }),
};
/**
 * @function getTokenFromAzure
 * @description Attempts to silently acquire an access token from Azure AD.
 * @param {IPublicClientApplication} instance - The MSAL Public Client Application instance.
 * @returns {Promise<string | null>} A promise that resolves to:
 *   - The access token (string) if successful.
 *   - `null` if no accounts are found or silent acquisition fails.
 * @throws Throws an error if an unexpected error occurs.
 */
export const getTokenFromAzure = async (instance: IPublicClientApplication) => {
  const accounts = instance.getAllAccounts();
  if (!accounts.length) {
    return null;
  }
  return await instance
    .acquireTokenSilent({
      ...AuthConfig.loginRequest,
      account: accounts[0],
    })
    .then((response: any) => {
      const cookieOptions: any = {};
      if (response?.expiresOn)
        cookieOptions.expires = new Date(new Date(response?.expiresOn).getTime() - 5 * 60000);
      setCookie('authProviderObjectId', response?.idTokenClaims?.oid || '', cookieOptions);
      setCookie('id_token', response.idToken, cookieOptions);
      setCookie('unique_id', response?.uniqueId, cookieOptions);
      setCookie(
        'session_info',
        response?.uniqueId + '$1' + dayjs().format('MMDDYYHH:mm:ss'),
        cookieOptions,
      );

      fbLogEvent(EVENT.SV_HOME, {
        screen: SCREEN.HOME,
        lead: LEAD.LOGIN_SUCCESS,
      });

      return response.idToken;
    })
    .catch((e: any) => {
      console.error('Error : ', e);
      toast(SERVER_ERROR_MESSAGES.unAuthorizedMsg);
      instance.logout();
    });
};
